/* Reveal control for the landing page sticky CTA.
 *
 * The bar itself is `position: sticky` on the section wrapper, so by default it simply appears
 * once the page scrolls to where the section sits. This lets a merchant delay that: hold the bar
 * back until a chosen element has scrolled off the top, or until a scroll distance is passed.
 *
 * The section only renders this element when a trigger is configured, and the matching CSS is
 * emitted alongside it — so with no trigger set, none of this is on the page and the bar behaves
 * exactly as it always has.
 */

const REVEALED_CLASS = 'vp-lp-cta-revealed';

class VpLpCtaReveal extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('.vp-lp-sticky-cta-section');
    if (!this.wrapper) return;

    this.pastStart = false;
    this.pastEnd = false;
    this.showTarget = null;
    this.hideTargets = [];

    switch (this.dataset.trigger) {
      case 'element':
        this.showTarget = this.find(this.dataset.selector);
        if (!this.showTarget) this.pastStart = true; // fail open
        break;
      case 'scroll':
        this.scrollDistance = Number(this.dataset.distance) || 0;
        break;
      default:
        this.pastStart = true;
    }

    this.hideTargets = this.findAll(this.dataset.hideSelector);
    this.showOffset = Number(this.dataset.offset) || 0;
    this.hideOffset = Number(this.dataset.hideOffset) || 0;

    let ticking = false;
    this.onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.check();
        ticking = false;
      });
    };

    // Capture catches scroll events from body or any nested scroller, not just window.
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.check();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
  }

  // Comma separated selectors are fine here; every match becomes a hide zone.
  findAll(selector) {
    try {
      return selector ? Array.from(document.querySelectorAll(selector.trim())) : [];
    } catch {
      return [];
    }
  }

  find(selector) {
    try {
      return selector ? document.querySelector(selector.trim()) : null;
    } catch {
      return null;
    }
  }

  check() {
    const vh = window.innerHeight;

    if (this.showTarget) {
      // Show once the top of the target reaches the top of the screen.
      this.pastStart = this.showTarget.getBoundingClientRect().top <= this.showOffset;
    } else if (this.dataset.trigger === 'scroll') {
      const y = document.scrollingElement?.scrollTop ?? window.scrollY;
      this.pastStart = y >= this.scrollDistance;
    }

    if (this.hideTargets.length) {
      // Hide while any target is on screen: it has entered from the bottom (with the offset as
      // a buffer) and has not yet fully left off the top. Once it is gone, the bar comes back.
      this.pastEnd = this.hideTargets.some((el) => {
        const rect = el.getBoundingClientRect();
        return rect.top <= vh - this.hideOffset && rect.bottom > 0;
      });
    }

        this.wrapper.classList.toggle(REVEALED_CLASS, this.pastStart && !this.pastEnd);
  }
}

if (!customElements.get('vp-lp-cta-reveal')) {
  customElements.define('vp-lp-cta-reveal', VpLpCtaReveal);
}